import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Receipt, Upload, Loader2, Search, Plus, ChevronRight,
  Banknote, Download, FileCode2, ArrowRight, History, CheckCheck,
  BarChart3, RotateCw, XCircle, AlertTriangle, Copy, Trash2, ArrowLeftRight,
  Layers, FileText, Check, X as XIcon, MoreHorizontal,
} from "lucide-react";
import api from "@/lib/api";

// ============================================================
// Status workflow étendu :
//   a_valider → a_payer → a_exporter → exportee → payee
//                                                ↘  rejetee
// ============================================================
const STATUT_LABELS = {
  brouillon: "Brouillon",
  a_valider: "À valider",
  validee: "Validée",
  a_payer: "À payer",
  a_exporter: "À exporter",
  exportee: "Exportée",
  payee: "Payée",
  rejetee: "Rejetée",
};
const STATUT_COLORS = {
  brouillon: "bg-gray-100 text-gray-600 border-gray-200",
  a_valider: "bg-amber-50 text-amber-700 border-amber-200",
  validee: "bg-blue-50 text-blue-700 border-blue-200",
  a_payer: "bg-orange-50 text-orange-700 border-orange-200",
  a_exporter: "bg-purple-50 text-purple-700 border-purple-200",
  exportee: "bg-slate-100 text-slate-700 border-slate-300",
  payee: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejetee: "bg-red-50 text-red-700 border-red-200",
};

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

export default function FacturesFournisseurs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [byStatut, setByStatut] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // iter210 — Import Excel de factures fournisseurs
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreview, setExcelPreview] = useState(null); // { parsed, errors_count, errors, preview }
  const [excelImporting, setExcelImporting] = useState(false);
  const excelInputRef = useRef(null);
  const [tab, setTab] = useState("a_valider");
  const [q, setQ] = useState("");
  const fileInputRef = useRef(null);

  // Sélection multiple (pour préparer/générer l'export banque)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);  // iter228 — audit du lot d'export

  const [showHistory, setShowHistory] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // iter154 — Détection de doublons sur "À valider"
  const [duplicates, setDuplicates] = useState({ groups: [], duplicate_ids: [], total_items_in_groups: 0 });
  const [duplicateMap, setDuplicateMap] = useState({}); // id → [{other_ref, other_id, key, type}]
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);

  // iter163.4 — Plan comptable pour édition inline compte_charge
  const [planComptes, setPlanComptes] = useState([]);
  useEffect(() => {
    api.get("/journal/plan-comptes").then((r) => {
      setPlanComptes(Array.isArray(r.data) ? r.data.filter((c) => c.actif !== false) : []);
    }).catch(() => { /* silent */ });
  }, []);

  const loadDuplicates = useCallback(async () => {
    if (tab !== "a_valider") {
      setDuplicates({ groups: [], duplicate_ids: [], total_items_in_groups: 0 });
      setDuplicateMap({});
      return;
    }
    try {
      const { data } = await api.get("/factures-fournisseurs/duplicates?statut=a_valider");
      setDuplicates(data);
      // Construit un index id → liste de "partenaires" dans le(s) groupe(s)
      const map = {};
      (data.groups || []).forEach((g) => {
        g.items.forEach((it) => {
          const partners = g.items
            .filter((x) => x.id !== it.id)
            .map((x) => ({
              other_id: x.id,
              other_ref: x.reference_interne,
              other_numero: x.numero_facture,
              type: g.type,
            }));
          if (!map[it.id]) map[it.id] = [];
          map[it.id].push(...partners);
        });
      });
      setDuplicateMap(map);
    } catch {
      // silent
    }
  }, [tab]);

  // iter164.1 — Tri : par défaut par échéance ASC dans onglet "à payer", création DESC ailleurs
  const [sortMode, setSortMode] = useState(null); // null=default, ou 'date_echeance_asc/desc' etc.
  useEffect(() => { setSortMode(null); /* reset au change d'onglet */ }, [tab]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("statut", tab);
      if (q) params.set("q", q);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      if (sortMode) params.set("sort", sortMode);
      const { data } = await api.get(`/factures-fournisseurs?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setByStatut(data.by_statut || {});
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [tab, q, page, sortMode]);

  // iter156 — Suppression unitaire d'une FF
  const handleDeleteFF = useCallback(async (f) => {
    if (!window.confirm(`Supprimer définitivement la facture ${f.reference_interne} (${f.numero_facture || 'sans n°'}) ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/factures-fournisseurs/${f.id}`);
      toast.success(`Facture ${f.reference_interne} supprimée`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  }, [reload]);

  // iter156 — Bulk delete des doublons (garde le 1er de chaque groupe)
  const handleBulkDeleteDuplicates = useCallback(async () => {
    try {
      // 1) Dry-run pour avoir le count exact
      const { data: dry } = await api.post("/factures-fournisseurs/bulk-delete-duplicates", {});
      if (!dry.would_delete_count) {
        toast.info("Aucun doublon à supprimer");
        return;
      }
      if (!window.confirm(`Supprimer ${dry.would_delete_count} doublon(s) ?\n(Le 1er de chaque groupe sera conservé.)\nAction irréversible.`)) return;
      const { data } = await api.post("/factures-fournisseurs/bulk-delete-duplicates", { confirm: true });
      toast.success(`${data.deleted_count} doublon(s) supprimé(s)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression doublons");
    }
  }, [reload]);

  // iter156 — Transfert FF → Dépenses rapides
  const handleTransferToDR = useCallback(async (f) => {
    if (!window.confirm(`Transférer la facture ${f.reference_interne} vers les Dépenses Rapides ?\nLa FF sera supprimée et une DR créée dans 'À valider'.`)) return;
    try {
      const { data } = await api.post(`/factures-fournisseurs/${f.id}/transfer-to-depenses-rapides`);
      toast.success(`Transféré vers DR ${data.to_dr_reference}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur transfert");
    }
  }, [reload]);

  useEffect(() => { setPage(1); setSelectedIds(new Set()); setShowOnlyDuplicates(false); }, [tab, q]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { loadDuplicates(); }, [loadDuplicates, items]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 20) { toast.error("Maximum 20 fichiers"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/factures-fournisseurs/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Facture importée — ${data.reference_interne}. Analyse OCR en cours…`);
      try {
        await api.post(`/factures-fournisseurs/${data.id}/ocr`);
        toast.success("Analyse OCR terminée — vérifiez les données");
      } catch {
        toast.error("OCR échoué — vous pouvez réessayer depuis la fiche");
      }
      navigate(`/factures-fournisseurs/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur import");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // iter210 — Import Excel : télécharge le template
  const handleExcelTemplateDownload = async () => {
    try {
      const res = await api.get("/factures-fournisseurs/import-excel/template", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Import_Factures_Fournisseurs.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur téléchargement template");
    }
  };

  // iter210 — Import Excel : aperçu (dry-run)
  const handleExcelPreview = async () => {
    if (!excelFile) { toast.error("Sélectionne un fichier Excel"); return; }
    setExcelImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", excelFile);
      const { data } = await api.post(
        "/factures-fournisseurs/import-excel?dry_run=true",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setExcelPreview(data);
      if (data.parsed === 0 && data.errors_count === 0) {
        toast.warning("Aucune ligne à importer (fichier vide ou uniquement la ligne d'exemple)");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur analyse fichier");
    } finally {
      setExcelImporting(false);
    }
  };

  // iter210 — Import Excel : confirmation (création réelle)
  const handleExcelImport = async () => {
    if (!excelFile) return;
    if (!excelPreview) { toast.error("Fais d'abord un aperçu"); return; }
    if (excelPreview.parsed === 0) { toast.error("Aucune ligne valide à importer"); return; }
    setExcelImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", excelFile);
      const { data } = await api.post(
        "/factures-fournisseurs/import-excel?dry_run=false",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const created = data.created || 0;
      if (created > 0) {
        toast.success(`${created} facture(s) importée(s) — statut « À valider »`);
      }
      const errs = data.errors_count || 0;
      if (errs > 0) {
        toast.warning(`${errs} ligne(s) ignorée(s) — voir les détails`);
      }
      setExcelImportOpen(false);
      setExcelFile(null);
      setExcelPreview(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur import");
    } finally {
      setExcelImporting(false);
    }
  };

  // Sélection
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const allVisibleSelected = items.length > 0 && items.every((it) => selectedIds.has(it.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((it) => it.id)));
  };

  const selectedItems = useMemo(
    () => items.filter((it) => selectedIds.has(it.id)),
    [items, selectedIds]
  );
  const selectedTotal = selectedItems.reduce((s, it) => s + (it.montant_ttc || 0), 0);
  // iter228 — Devises présentes dans la sélection (un lot pain.001 = une seule devise)
  const selectedDevises = useMemo(
    () => [...new Set(selectedItems.map((it) => (it.devise || "CHF").toUpperCase()))].sort(),
    [selectedItems]
  );

  // Actions workflow
  const preparerExport = async () => {
    if (selectedIds.size === 0) return;
    setExportLoading(true);
    try {
      const { data } = await api.post("/factures-fournisseurs/preparer-export", {
        ids: Array.from(selectedIds),
      });
      const nb = data.preparees?.length || 0;
      const refused = data.refusees?.length || 0;
      if (nb) toast.success(`${nb} facture(s) basculée(s) en "à exporter"`);
      if (refused) toast.error(`${refused} facture(s) refusée(s) (IBAN manquant ou statut invalide)`);
      setSelectedIds(new Set());
      setTab("a_exporter");
      reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Erreur préparation");
    } finally { setExportLoading(false); }
  };

  const genererExport = async (forcer = false) => {
    if (selectedIds.size === 0) return;
    // iter228 — Contrôle du lot AVANT génération : devises mélangées, QR-IBAN sans
    // référence, IBAN divergent du registre fournisseurs.
    if (!forcer) {
      setExportLoading(true);
      try {
        const { data } = await api.post("/factures-fournisseurs/bank-exports/preflight", {
          ids: Array.from(selectedIds),
        });
        if (!data.ok) {
          setPreflight(data);
          return;
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Contrôle du lot impossible");
        return;
      } finally { setExportLoading(false); }
    }
    if (!window.confirm(`Générer un fichier pain.001 ISO20022 pour ${selectedIds.size} facture(s) (total : ${fmtChf(selectedTotal)} CHF) ?\n\nLes factures basculeront en statut "Exportée". Le fichier XML sera téléchargé pour upload sur l'e-banking.`)) return;
    setExportLoading(true);
    try {
      const { data } = await api.post("/factures-fournisseurs/bank-exports", {
        ids: Array.from(selectedIds),
        forcer_alertes: forcer,
      });
      toast.success(`Export ${data.msg_id} généré — ${data.nb_transactions} paiements, ${fmtChf(data.total)} CHF`);
      // Télécharge le XML
      await telechargerXml(data.export_id, data.filename);
      setPreflight(null);
      setSelectedIds(new Set());
      setTab("exportee");
      reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && (detail.blocants || detail.alertes)) {
        setPreflight(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur génération export");
      }
    } finally { setExportLoading(false); }
  };

  // iter163.4 — Validation en masse (checkbox + bouton "Valider N")
  const bulkValider = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Valider ${selectedIds.size} facture(s) sélectionnée(s) ? Chaque facture doit avoir un compte de charge et les champs obligatoires (n° facture, date, montant) remplis.`)) return;
    setExportLoading(true);
    try {
      const { data } = await api.post("/factures-fournisseurs/bulk-validate", {
        ids: Array.from(selectedIds),
      });
      if (data.success > 0) toast.success(`${data.success} facture(s) validée(s)`);
      if (data.errors > 0) {
        const firstErr = data.results.find((r) => !r.ok);
        toast.error(`${data.errors} en erreur — première : ${firstErr?.error?.slice(0, 90) || "inconnu"}`);
      }
      setSelectedIds(new Set());
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur validation en masse");
    } finally { setExportLoading(false); }
  };

  // iter163.4 — Edition inline compte_charge (via PATCH direct)
  const updateCompteCharge = async (fid, compte) => {
    try {
      await api.patch(`/factures-fournisseurs/${fid}`, { compte_charge: compte });
      // Optimistic update local
      const newItems = items.map((it) => it.id === fid ? { ...it, compte_charge: compte } : it);
      setItems(newItems);
      toast.success("Compte mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur PATCH compte");
    }
  };

  const telechargerXml = async (exportId, filename) => {
    try {
      const r = await api.get(`/factures-fournisseurs/bank-exports/${exportId}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `pain001_${exportId}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Téléchargement échoué");
    }
  };

  const Counter = ({ statut }) => {
    const n = byStatut[statut] || 0;
    if (!n) return null;
    return <Badge className="ml-2 bg-gray-200 text-gray-700 border-0 h-5 px-1.5 text-[10px]">{n}</Badge>;
  };

  // Détermine si la sélection est valide pour l'action (selon le tab)
  const canPreparerExport = tab === "a_payer" && selectedIds.size > 0;
  const canGenererExport = tab === "a_exporter" && selectedIds.size > 0;
  const showCheckboxColumn = tab === "a_payer" || tab === "a_exporter" || tab === "a_valider";
  const canBulkValidate = tab === "a_valider" && selectedIds.size > 0;

  const FF_TABS = [
    { value: "all", label: "Toutes", statut: null },
    { value: "a_valider", label: "À valider", statut: "a_valider" },
    { value: "a_payer", label: "À payer", statut: "a_payer" },
    { value: "a_exporter", label: "À exporter", statut: "a_exporter" },
    { value: "exportee", label: "Exportées", statut: "exportee" },
    { value: "payee", label: "Payées", statut: "payee" },
    { value: "rejetee", label: "Rejetées", statut: "rejetee" },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="page-factures-fournisseurs">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-[#52a675]" />
            Factures fournisseurs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Import · OCR IA · Validation · Export bancaire ISO 20022 · Rapprochement CAMT
          </p>
        </div>
        {/* iter254 — actions harmonisées : 1 action principale + 1 menu, identique
            sur mobile, tablette et ordinateur (plus de bande de 7 boutons). */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf"
            className="hidden"
            onChange={handleUpload}
            data-testid="ff-upload-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#52a675] hover:bg-[#3d8a5c] text-white flex-1 sm:flex-none"
            data-testid="ff-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Analyse…" : "Importer une facture"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex-shrink-0" data-testid="ff-actions-menu">
                <MoreHorizontal className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onClick={() => setBulkOpen(true)} data-testid="ff-bulk-upload-btn">
                <Layers className="w-4 h-4 mr-2 text-[#3d8a5c]" /> Import en lot
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setExcelImportOpen(true); setExcelFile(null); setExcelPreview(null); }}
                data-testid="ff-excel-import-btn"
              >
                <FileText className="w-4 h-4 mr-2 text-emerald-700" /> Importer depuis Excel
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/achats/statistiques")} data-testid="ff-stats-btn">
                <BarChart3 className="w-4 h-4 mr-2 text-gray-500" /> Statistiques
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowHistory(true)} data-testid="ff-history-btn">
                <History className="w-4 h-4 mr-2 text-gray-500" /> Historique exports
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowAudit(true)} data-testid="ff-audit-btn">
                <AlertTriangle className="w-4 h-4 mr-2 text-amber-600" /> Audit compta
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Drag & drop */}
      <Card
        className="border-2 border-dashed border-gray-300 hover:border-[#52a675] transition-colors p-6 mb-5 text-center cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const dt = new DataTransfer();
          Array.from(e.dataTransfer.files || []).forEach((f) => dt.items.add(f));
          if (fileInputRef.current) {
            fileInputRef.current.files = dt.files;
            handleUpload({ target: { files: dt.files } });
          }
        }}
        data-testid="ff-drop-zone"
      >
        <div className="flex items-center justify-center gap-3 text-gray-500">
          <Upload className="w-6 h-6" />
          <span className="text-sm">
            Glissez vos PDF / photos de factures ici, ou{" "}
            <span className="text-[#52a675] font-semibold underline">cliquez pour parcourir</span>
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-2">PDF, JPG, PNG, HEIC · Max 20 fichiers · OCR automatique</p>
      </Card>

      {/* Filtre recherche */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (réf, fournisseur, n°…)"
            className="pl-10"
            data-testid="ff-search"
          />
        </div>
      </div>

      {/* Tabs étendus pour workflow ISO20022 */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        {/* iter254 — mobile : la bande d'onglets devient un menu déroulant */}
        <div className="sm:hidden mb-3">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-full" data-testid="ff-tab-menu">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FF_TABS.map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`ff-tab-menu-${t.value}`}>
                  {t.label}{(byStatut[t.statut] || 0) > 0 ? ` (${byStatut[t.statut]})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TabsList className="hidden sm:flex bg-white border border-gray-200 p-1 h-auto flex-wrap" data-testid="ff-tabs">
          <TabsTrigger value="all" data-testid="ff-tab-all">Toutes <Counter statut={null} /></TabsTrigger>
          <TabsTrigger value="a_valider" data-testid="ff-tab-a-valider">À valider <Counter statut="a_valider" /></TabsTrigger>
          <TabsTrigger value="a_payer" data-testid="ff-tab-a-payer">À payer <Counter statut="a_payer" /></TabsTrigger>
          <TabsTrigger value="a_exporter" data-testid="ff-tab-a-exporter">À exporter <Counter statut="a_exporter" /></TabsTrigger>
          <TabsTrigger value="exportee" data-testid="ff-tab-exportee">Exportées <Counter statut="exportee" /></TabsTrigger>
          <TabsTrigger value="payee" data-testid="ff-tab-payee">Payées <Counter statut="payee" /></TabsTrigger>
          <TabsTrigger value="rejetee" data-testid="ff-tab-rejetee">Rejetées <Counter statut="rejetee" /></TabsTrigger>
        </TabsList>

        {/* iter154 — Bandeau "Doublons potentiels" sur l'onglet À valider */}
        {tab === "a_valider" && duplicates.total_items_in_groups > 0 && (
          <div
            className="mt-3 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 sm:px-4 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between"
            data-testid="ff-duplicates-banner"
          >
            {/* iter250 — mobile : texte sur 2 lignes puis boutons pleine largeur */}
            <div className="flex items-start gap-2 text-sm min-w-0">
              <Copy className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <span className="font-semibold text-amber-900" data-testid="ff-duplicates-count">
                  {duplicates.total_items_in_groups} doublon(s) potentiel(s)
                </span>
                <span className="text-amber-800 block sm:inline">
                  <span className="hidden sm:inline"> — </span>
                  sur {duplicates.groups.length} groupe(s) · même n° de facture ou même référence QR/RF
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:flex-shrink-0">
              <Button
                size="sm"
                variant={showOnlyDuplicates ? "default" : "outline"}
                className={showOnlyDuplicates ? "bg-amber-600 hover:bg-amber-700 w-full sm:w-auto" : "border-amber-400 text-amber-800 hover:bg-amber-100 w-full sm:w-auto"}
                onClick={() => setShowOnlyDuplicates((v) => !v)}
                data-testid="ff-toggle-duplicates"
              >
                {showOnlyDuplicates ? "Afficher tout" : (
                  <>
                    <span className="sm:hidden">Voir les doublons</span>
                    <span className="hidden sm:inline">Afficher uniquement les doublons</span>
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-400 text-red-800 hover:bg-red-100 w-full sm:w-auto"
                onClick={handleBulkDeleteDuplicates}
                data-testid="ff-bulk-delete-duplicates"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                <span className="sm:hidden">Nettoyer</span>
                <span className="hidden sm:inline">Supprimer les doublons (garder le 1er)</span>
              </Button>
            </div>
          </div>
        )}

        {/* Bandeau d'actions lié à la sélection */}
        {(canPreparerExport || canGenererExport || canBulkValidate) && (          <div
            className="mt-3 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5"
            data-testid="ff-selection-bar"
          >
            <div className="text-sm">
              <span className="font-semibold text-emerald-900">{selectedIds.size}</span>{" "}
              <span className="text-emerald-800">facture(s) sélectionnée(s) ·</span>{" "}
              <span className="font-mono font-semibold text-emerald-900">{fmtChf(selectedTotal)} CHF</span>
              {selectedDevises.length > 1 && (
                <div className="mt-1 text-xs text-red-700 font-semibold flex items-center gap-1" data-testid="ff-devises-mix-warning">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Devises mélangées ({selectedDevises.join(" + ")}) — la banque refuse le fichier, exportez une devise à la fois
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm" variant="ghost"
                onClick={() => setSelectedIds(new Set())}
                data-testid="ff-clear-selection"
              >
                Annuler
              </Button>
              {canBulkValidate && (
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={bulkValider}
                  disabled={exportLoading}
                  data-testid="ff-bulk-validate-btn"
                >
                  {exportLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5 mr-1.5" />}
                  Valider {selectedIds.size} facture{selectedIds.size > 1 ? "s" : ""}
                </Button>
              )}
              {canPreparerExport && (
                <Button
                  size="sm"
                  className="bg-[#52a675] hover:bg-[#3d8a5c]"
                  onClick={preparerExport}
                  disabled={exportLoading}
                  data-testid="ff-preparer-export-btn"
                >
                  {exportLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5 mr-1.5" />}
                  Préparer export banque
                </Button>
              )}
              {canGenererExport && (
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => genererExport(false)}
                  disabled={exportLoading}
                  data-testid="ff-generer-export-btn"
                >
                  {exportLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileCode2 className="w-3.5 h-3.5 mr-1.5" />}
                  Générer fichier banque (pain.001)
                </Button>
              )}
            </div>
          </div>
        )}

        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : items.length === 0 ? (
            <Card className="p-12 text-center text-gray-400">
              <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Aucune facture fournisseur</p>
              <Button
                variant="outline" size="sm" className="mt-4"
                onClick={() => fileInputRef.current?.click()}
                data-testid="ff-empty-upload-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Importer une facture
              </Button>
            </Card>
          ) : (
            <Card className="p-0 overflow-hidden">
              {/* iter256 — la table (avec en-tête) est réservée à l'ordinateur */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    {showCheckboxColumn && (
                      <th className="px-3 py-2 w-8">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={toggleSelectAll}
                          data-testid="ff-select-all"
                        />
                      </th>
                    )}
                    <th className="px-3 py-2 text-left">Référence</th>
                    <th className="px-3 py-2 text-left">Fournisseur</th>
                    <th className="px-3 py-2 text-left">N° facture</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th
                      className="px-3 py-2 text-left cursor-pointer hover:text-gray-800 select-none"
                      onClick={() => {
                        // Toggle date_echeance asc → desc → default
                        setSortMode((m) => (m === "date_echeance_asc" ? "date_echeance_desc" : m === "date_echeance_desc" ? null : "date_echeance_asc"));
                      }}
                      data-testid="ff-th-echeance"
                      title="Cliquer pour trier par date d'échéance"
                    >
                      Échéance
                      {sortMode === "date_echeance_asc" && <span className="ml-1">↑</span>}
                      {sortMode === "date_echeance_desc" && <span className="ml-1">↓</span>}
                      {!sortMode && tab === "a_payer" && <span className="ml-1 text-[10px] text-gray-400">↑</span>}
                    </th>
                    <th className="px-3 py-2 text-right">Montant TTC</th>
                    <th className="px-3 py-2 text-left">Compte</th>
                    <th className="px-3 py-2 text-center">Statut</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items
                    .filter((f) => !showOnlyDuplicates || (duplicateMap[f.id] && duplicateMap[f.id].length > 0))
                    .map((f) => {
                    const dupPartners = duplicateMap[f.id] || [];
                    const isDup = dupPartners.length > 0;
                    return (
                    <tr
                      key={f.id}
                      className={`border-t border-gray-100 hover:bg-gray-50 ${
                        selectedIds.has(f.id) ? "bg-emerald-50/40" : ""
                      } ${isDup ? "bg-amber-50/30" : ""}`}
                      data-testid={`ff-row-${f.id}`}
                    >
                      {showCheckboxColumn && (
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(f.id)}
                            onCheckedChange={() => toggleSelect(f.id)}
                            data-testid={`ff-select-${f.id}`}
                          />
                        </td>
                      )}
                      <td
                        className="px-3 py-2 font-mono text-xs text-gray-700 cursor-pointer"
                        onClick={() => navigate(`/factures-fournisseurs/${f.id}`)}
                      >
                        <div className="flex items-center gap-1.5">
                          {isDup && (
                            <span
                              className="inline-flex items-center"
                              title={`Doublon potentiel avec : ${dupPartners.map(p => p.other_ref + (p.other_numero ? ` (${p.other_numero})` : '')).join(', ')}\nCritère : ${dupPartners[0].type}`}
                              data-testid={`ff-dup-badge-${f.id}`}
                            >
                              <Copy className="w-3.5 h-3.5 text-amber-600" />
                            </span>
                          )}
                          {f.reference_interne}
                        </div>
                      </td>
                      <td
                        className="px-3 py-2 font-medium text-gray-900 cursor-pointer"
                        onClick={() => navigate(`/factures-fournisseurs/${f.id}`)}
                      >
                        {f.fournisseur_nom || <span className="text-gray-300 italic">non renseigné</span>}
                      </td>
                      <td
                        className="px-3 py-2 text-gray-700 cursor-pointer"
                        onClick={() => navigate(`/factures-fournisseurs/${f.id}`)}
                      >
                        {f.numero_facture || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{fmtDate(f.date_facture)}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{fmtDate(f.date_echeance)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">
                        {f.montant_ttc != null ? `${fmtChf(f.montant_ttc)} ${f.devise || "CHF"}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                        {tab === "a_valider" ? (
                          <select
                            value={f.compte_charge || ""}
                            onChange={(e) => updateCompteCharge(f.id, e.target.value)}
                            className={`w-full border rounded h-7 px-1 text-xs bg-white font-mono ${!f.compte_charge ? "border-amber-400 bg-amber-50" : "border-gray-200"}`}
                            data-testid={`ff-compte-inline-${f.id}`}
                            title={f.compte_charge ? planComptes.find((c) => c.numero === f.compte_charge)?.libelle : "Choisir un compte comptable"}
                          >
                            <option value="">— Aucun —</option>
                            {planComptes.map((c) => (
                              <option key={c.numero} value={c.numero}>
                                {c.numero} · {c.libelle}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-gray-600 font-mono">{f.compte_charge || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`text-[10px] ${STATUT_COLORS[f.statut] || STATUT_COLORS.brouillon}`}>
                          {STATUT_LABELS[f.statut] || f.statut}
                        </Badge>
                        {/* Indicateur secondaire pour les exportées (en attente CAMT vs payée) */}
                        {f.statut === "exportee" && (
                          <div className="text-[9px] text-slate-500 mt-0.5">en attente banque</div>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-gray-400"
                      >
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {tab === "a_valider" && (
                            <>
                              <button
                                onClick={() => handleTransferToDR(f)}
                                className="p-1 rounded hover:bg-blue-100 text-blue-600"
                                title="Transférer vers Dépenses Rapides"
                                data-testid={`ff-transfer-dr-${f.id}`}
                              >
                                <ArrowLeftRight className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteFF(f)}
                                className="p-1 rounded hover:bg-red-100 text-red-600"
                                title="Supprimer cette facture"
                                data-testid={`ff-delete-${f.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => navigate(`/factures-fournisseurs/${f.id}`)}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Voir le détail"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {/* iter256 — mobile : pas d'en-tête de table, 2 lignes par facture
                  (référence, date, fournisseur, n° facture, statut, montant TTC) */}
              <div className="md:hidden divide-y divide-gray-100" data-testid="ff-list-mobile">
                {items
                  .filter((f) => !showOnlyDuplicates || (duplicateMap[f.id] && duplicateMap[f.id].length > 0))
                  .map((f) => {
                    const isDup = (duplicateMap[f.id] || []).length > 0;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => navigate(`/factures-fournisseurs/${f.id}`)}
                        className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 active:bg-gray-100 ${isDup ? "bg-amber-50/40" : ""}`}
                        data-testid={`ff-card-${f.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {isDup && <Copy className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />}
                            <code className="text-xs font-mono font-semibold text-gray-800">{f.reference_interne}</code>
                            <span className="text-[11px] text-gray-500 whitespace-nowrap">{fmtDate(f.date_facture)}</span>
                          </div>
                          <Badge className={`text-[10px] flex-shrink-0 ${STATUT_COLORS[f.statut] || STATUT_COLORS.brouillon}`}>
                            {STATUT_LABELS[f.statut] || f.statut}
                          </Badge>
                        </div>
                        <div className="flex items-end justify-between gap-2 min-w-0 mt-0.5">
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900 font-medium truncate">
                              {f.fournisseur_nom || <span className="text-gray-300 italic">non renseigné</span>}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {f.numero_facture || "—"}
                              {f.compte_charge ? ` · ${f.compte_charge}` : ""}
                            </div>
                          </div>
                          <div className="font-mono text-sm font-semibold text-gray-900 flex-shrink-0 whitespace-nowrap">
                            {f.montant_ttc != null ? `${fmtChf(f.montant_ttc)} ${f.devise || "CHF"}` : "—"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
              {/* Pagination */}
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-600">
                  <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                      data-testid="ff-prev-page">
                      Précédent
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setPage((p) => p + 1)} disabled={page * PAGE_SIZE >= total}
                      data-testid="ff-next-page">
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <HistoriqueExportsDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        onDownload={telechargerXml}
      />
      <AuditDesynchroDialog
        open={showAudit}
        onClose={() => setShowAudit(false)}
        onRepaired={() => fetch()}
      />
      <BulkUploadDialog
        open={bulkOpen}
        onClose={() => { setBulkOpen(false); reload(); }}
      />

      {/* iter228 — Contrôle du lot avant génération du fichier banque */}
      <Dialog open={!!preflight} onOpenChange={(o) => !o && setPreflight(null)}>
        <DialogContent className="max-w-2xl" data-testid="ff-preflight-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={preflight?.blocants?.length ? "w-5 h-5 text-red-600" : "w-5 h-5 text-amber-500"} />
              {preflight?.blocants?.length ? "Export bloqué" : "Vérification avant export"}
            </DialogTitle>
            <DialogDescription>
              {preflight?.blocants?.length
                ? "La banque rejetterait ce fichier. Corrigez les points ci-dessous puis relancez la génération."
                : "Points à confirmer avant de générer le fichier pain.001."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            {preflight?.devises && Object.keys(preflight.devises).length > 0 && (
              <div className="text-xs text-gray-600">
                Devises du lot :{" "}
                {Object.entries(preflight.devises).map(([d, n]) => `${d} (${n})`).join(" · ")}
              </div>
            )}
            {(preflight?.blocants || []).map((b, i) => (
              <div key={`b${i}`} className="border border-red-200 bg-red-50 rounded p-2.5" data-testid={`ff-preflight-blocant-${i}`}>
                <div className="text-xs font-semibold text-red-900">
                  {b.reference} {b.fournisseur !== "—" && `· ${b.fournisseur}`}
                </div>
                <div className="text-xs text-red-800 mt-0.5">{b.message}</div>
              </div>
            ))}
            {(preflight?.alertes || []).map((a, i) => (
              <div key={`a${i}`} className="border border-amber-200 bg-amber-50 rounded p-2.5" data-testid={`ff-preflight-alerte-${i}`}>
                <div className="text-xs font-semibold text-amber-900">{a.reference} · {a.fournisseur}</div>
                <div className="text-xs text-amber-800 mt-0.5">{a.message}</div>
              </div>
            ))}
            {(preflight?.resolus || []).map((r, i) => (
              <div key={`r${i}`} className="border border-blue-200 bg-blue-50 rounded p-2.5 text-xs text-blue-800" data-testid={`ff-preflight-resolu-${i}`}>
                {r.reference} · {r.fournisseur} — {r.motif} ({r.iban})
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreflight(null)} data-testid="ff-preflight-close">
              Fermer
            </Button>
            {preflight?.forcable && (
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                disabled={exportLoading}
                onClick={() => genererExport(true)}
                data-testid="ff-preflight-force"
              >
                {exportLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileCode2 className="w-3.5 h-3.5 mr-1.5" />}
                J&apos;ai vérifié — générer quand même
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* iter210 — Dialog d'import Excel de factures fournisseurs */}
      <Dialog open={excelImportOpen} onOpenChange={(o) => !o && !excelImporting && setExcelImportOpen(false)}>
        <DialogContent className="max-w-3xl" data-testid="ff-excel-import-dialog">
          <DialogHeader>
            <DialogTitle>Importer des factures fournisseurs depuis Excel</DialogTitle>
            <DialogDescription>
              Format .xlsx avec une ligne par facture. Les factures sont créées en statut « À valider »
              — aucun OCR n&apos;est déclenché. Un compta doit ensuite les valider pour générer les écritures.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Étape 1 — Template */}
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded p-3">
              <div className="text-sm">
                <div className="font-medium text-emerald-900">1. Télécharge le template</div>
                <div className="text-xs text-emerald-700">Remplis-le avec tes factures (1 ligne = 1 facture).</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExcelTemplateDownload}
                data-testid="ff-excel-template-download"
                className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
              >
                <Download className="w-4 h-4 mr-1" /> Télécharger le modèle
              </Button>
            </div>

            {/* Étape 2 — Fichier */}
            <div>
              <div className="text-sm font-medium mb-1">2. Sélectionne ton fichier rempli</div>
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setExcelFile(f);
                  setExcelPreview(null);
                }}
                className="text-sm"
                data-testid="ff-excel-file-input"
              />
              {excelFile && (
                <div className="text-xs text-gray-600 mt-1">
                  <FileText className="w-3 h-3 inline mr-1" />
                  {excelFile.name} ({Math.round(excelFile.size / 1024)} Ko)
                </div>
              )}
            </div>

            {/* Étape 3 — Aperçu */}
            {excelPreview && (
              <div className="border rounded p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">
                    Aperçu : <span className="text-emerald-700">{excelPreview.parsed} valide(s)</span>
                    {excelPreview.errors_count > 0 && (
                      <span className="text-red-600 ml-2">, {excelPreview.errors_count} erreur(s)</span>
                    )}
                  </div>
                </div>
                {excelPreview.preview?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border rounded bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Ligne</th>
                          <th className="px-2 py-1 text-left">Fournisseur</th>
                          <th className="px-2 py-1 text-left">N° facture</th>
                          <th className="px-2 py-1 text-left">Date</th>
                          <th className="px-2 py-1 text-right">TTC</th>
                          <th className="px-2 py-1 text-left">Compte</th>
                        </tr>
                      </thead>
                      <tbody data-testid="ff-excel-preview-tbody">
                        {excelPreview.preview.map((p) => (
                          <tr key={p.source_row} className="border-t">
                            <td className="px-2 py-1 text-gray-500">{p.source_row}</td>
                            <td className="px-2 py-1">{p.fournisseur_nom}</td>
                            <td className="px-2 py-1 font-mono">{p.numero_facture}</td>
                            <td className="px-2 py-1">{p.date_facture}</td>
                            <td className="px-2 py-1 text-right font-medium">
                              {fmtChf(p.montant_ttc)} {p.devise}
                            </td>
                            <td className="px-2 py-1">{p.compte_charge}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {excelPreview.errors?.length > 0 && (
                  <div className="mt-3 border border-red-200 rounded bg-red-50 p-2 max-h-40 overflow-y-auto" data-testid="ff-excel-errors">
                    <div className="text-xs font-medium text-red-800 mb-1">Erreurs détectées :</div>
                    <ul className="text-xs text-red-700 space-y-0.5">
                      {excelPreview.errors.slice(0, 30).map((e, i) => (
                        <li key={i}>
                          <span className="font-mono">L{e.row}</span>
                          {e.field && <> — <b>{e.field}</b></>}
                          {" : "}
                          {e.message}
                        </li>
                      ))}
                      {excelPreview.errors.length > 30 && (
                        <li className="italic">… et {excelPreview.errors.length - 30} autre(s)</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setExcelImportOpen(false)}
              disabled={excelImporting}
              data-testid="ff-excel-cancel"
            >
              Annuler
            </Button>
            {!excelPreview ? (
              <Button
                onClick={handleExcelPreview}
                disabled={!excelFile || excelImporting}
                data-testid="ff-excel-preview-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {excelImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Analyser le fichier
              </Button>
            ) : (
              <Button
                onClick={handleExcelImport}
                disabled={excelImporting || (excelPreview.parsed || 0) === 0}
                data-testid="ff-excel-confirm-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {excelImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
                Importer {excelPreview.parsed} facture{(excelPreview.parsed || 0) > 1 ? "s" : ""}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Historique des exports bancaires
// ============================================================
function HistoriqueExportsDialog({ open, onClose, onDownload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(null);
  const [annulerLoading, setAnnulerLoading] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.get("/factures-fournisseurs/bank-exports")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open]);

  const regenerer = async (exportId, filename) => {
    setRegenLoading(exportId);
    try {
      const { data } = await api.post(`/factures-fournisseurs/bank-exports/${exportId}/regenerer`);
      toast.success(`Fichier régénéré (${data.nb_transactions} transactions) — téléchargement…`);
      await onDownload(exportId, data.filename || filename);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Régénération échouée");
    } finally {
      setRegenLoading(null);
    }
  };

  const annuler = async (exportId, nbTx) => {
    if (!window.confirm(
      `Annuler cet export ?\n\n` +
      `Les ${nbTx} facture(s) repasseront en statut "À exporter" et vous pourrez refaire un nouvel export propre.\n\n` +
      `(Les factures déjà confirmées payées par la banque ne sont pas touchées.)`
    )) return;
    setAnnulerLoading(exportId);
    try {
      const { data } = await api.post(`/factures-fournisseurs/bank-exports/${exportId}/annuler`);
      toast.success(
        `${data.nb_restaurees} facture(s) restaurée(s) en "À exporter"` +
        (data.nb_non_eligibles ? ` (${data.nb_non_eligibles} déjà payée(s) non touchée(s))` : ""),
        { duration: 5000 },
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annulation échouée");
    } finally {
      setAnnulerLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ff-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-purple-600" />
            Historique des exports bancaires (pain.001)
          </DialogTitle>
          <DialogDescription>
            Fichiers ISO 20022 générés pour upload sur l&apos;e-banking. 3 actions possibles :
            <strong className="text-gray-700"> Télécharger</strong> le XML d&apos;origine,
            <strong className="text-amber-700"> Régénérer</strong> le XML avec le code actuel (rejet banque corrigé),
            <strong className="text-red-700"> Annuler</strong> pour repasser les factures en « À exporter » et refaire un lot.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-10 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Aucun export généré pour le moment.</div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const isAnnule = it.statut_banque === "annule";
              return (
                <Card
                  key={it.id}
                  className={`p-3 ${isAnnule ? "opacity-60 bg-gray-50" : ""}`}
                  data-testid={`ff-export-${it.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-gray-700 truncate">
                        {it.msg_id}
                        {isAnnule && <Badge className="ml-2 bg-red-100 text-red-700 border-0 text-[10px]">Annulé</Badge>}
                      </div>
                      <div className="text-sm font-semibold text-gray-900 mt-0.5">
                        {it.nb_transactions} paiement(s) · {fmtChf(it.total_montant)} CHF
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Exécution {fmtDate(it.date_execution)} · Créé {fmtDate(it.created_at?.slice(0, 10))} par {it.created_by_nom}
                      </div>
                      {it.regenere_at && (
                        <div className="text-[10px] text-amber-700 mt-0.5">
                          ⟳ Régénéré le {fmtDate(it.regenere_at?.slice(0, 10))} (msgId précédent : {it.msg_id_precedent || "?"})
                        </div>
                      )}
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        Factures : {(it.facture_refs || []).join(", ")}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {it.telecharge_at && (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                          <CheckCheck className="w-3 h-3 mr-1" /> Téléchargé
                        </Badge>
                      )}
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onDownload(it.id, it.filename)}
                        disabled={isAnnule}
                        data-testid={`ff-export-download-${it.id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> Télécharger XML
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => regenerer(it.id, it.filename)}
                        disabled={regenLoading === it.id || isAnnule}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`ff-export-regenerer-${it.id}`}
                        title="Reconstruit le XML avec le code actuel (utile en cas de rejet banque)"
                      >
                        {regenLoading === it.id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <RotateCw className="w-3.5 h-3.5 mr-1" />
                        )}
                        Régénérer
                      </Button>
                      {!isAnnule && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => annuler(it.id, it.nb_transactions)}
                          disabled={annulerLoading === it.id}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`ff-export-annuler-${it.id}`}
                          title="Repasse les factures en À exporter pour refaire un lot propre"
                        >
                          {annulerLoading === it.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                          )}
                          Annuler l&apos;export
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Audit comptable : factures fournisseurs désynchronisées
// ============================================================
function AuditDesynchroDialog({ open, onClose, onRepaired }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reparing, setReparing] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/factures-fournisseurs/audit/desynchronisees")
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Audit échoué");
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open]);

  const reparerTout = async () => {
    if (!window.confirm(
      `Régénérer les écritures de ${items.length} facture(s) ?\n\n` +
      `Les anciennes écritures comptables seront supprimées et recréées avec les montants actuels. ` +
      `Cette opération est tracée dans l'audit log et ne touche pas aux exercices clôturés.`
    )) return;
    setReparing(true);
    try {
      const { data } = await api.post("/factures-fournisseurs/audit/reparer-desynchronisees");
      toast.success(
        `${data.nb_reparees}/${data.total_traitees} facture(s) réparée(s)` +
        (data.nb_echouees ? ` · ${data.nb_echouees} en échec` : ""),
        { duration: 6000 },
      );
      refresh();
      if (onRepaired) onRepaired();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réparation bulk échouée");
    } finally {
      setReparing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="ff-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Audit comptable — factures désynchronisées
          </DialogTitle>
          <DialogDescription>
            Liste des factures fournisseurs dont les <strong>écritures comptables</strong> ne correspondent plus aux montants enregistrés
            (cas typique après une modification manuelle sans régénération). Réparer = supprimer + recréer les écritures.
            Les exercices clôturés sont exclus.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-10 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-emerald-600 text-sm font-semibold">
            ✅ Toutes les factures sont synchronisées. Aucune réparation nécessaire.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-amber-800 font-semibold">
                {items.length} facture(s) désynchronisée(s) détectée(s)
              </div>
              <Button
                size="sm"
                onClick={reparerTout}
                disabled={reparing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ff-audit-reparer-tout-btn"
              >
                {reparing ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Réparation…</>
                ) : (
                  <><RotateCw className="w-3.5 h-3.5 mr-1" /> Tout réparer ({items.length})</>
                )}
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it) => (
                <Card key={it.id} className="p-3 border-amber-200 bg-amber-50/40" data-testid={`ff-audit-row-${it.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-gray-700">{it.reference_interne}</div>
                      <div className="text-sm font-semibold text-gray-900">{it.fournisseur_nom}</div>
                      <div className="text-xs text-gray-500">
                        Facture {it.numero_facture || "?"} · {fmtDate(it.date_facture)} · {it.statut}
                      </div>
                    </div>
                    <div className="text-xs font-mono text-right text-gray-700">
                      <div>Facture : HT {fmtChf(it.ht_facture)} · TVA {fmtChf(it.tva_facture)} · <strong>TTC {fmtChf(it.ttc_facture)}</strong></div>
                      <div className="text-amber-700">Écritures : HT {fmtChf(it.ec_total_ht)} · TVA {fmtChf(it.ec_total_tva)} · <strong>TTC {fmtChf(it.ec_total_ttc)}</strong></div>
                      <div className="text-red-700 font-bold mt-0.5">Écart TTC : {it.ecart_ttc >= 0 ? "+" : ""}{fmtChf(it.ecart_ttc)} CHF</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================
// iter163 — Import en lot (1 PDF = 1 facture, OCR en arrière-plan)
// ============================================================
function BulkUploadDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);       // { file, status: 'pending' }
  const [results, setResults] = useState([]);   // { id, filename, ocr_status, ... }
  const [uploading, setUploading] = useState(false);
  const [polling, setPolling] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFiles([]); setResults([]); setUploading(false); setPolling(false);
    }
  }, [open]);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const combined = [...files, ...arr.map((f) => ({ file: f }))];
    if (combined.length > 20) {
      toast.error("Maximum 20 fichiers par lot");
      return;
    }
    setFiles(combined);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const startUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f.file));
      const { data } = await api.post("/factures-fournisseurs/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data.created || []);
      toast.success(`${data.success}/${data.total} facture(s) créée(s) — analyse OCR en cours…`);
      setPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'import en lot");
    } finally {
      setUploading(false);
    }
  };

  // Polling toutes les 3s tant qu'il reste queued/running
  useEffect(() => {
    if (!polling || !results.length) return;
    const pending = results.filter((r) => r.id && (r.ocr_status === "queued" || r.ocr_status === "running"));
    if (!pending.length) { setPolling(false); return; }
    const t = setTimeout(async () => {
      try {
        const updated = await Promise.all(
          results.map(async (r) => {
            if (!r.id || (r.ocr_status !== "queued" && r.ocr_status !== "running")) return r;
            try {
              const { data } = await api.get(`/factures-fournisseurs/${r.id}`);
              return {
                ...r,
                ocr_status: data.ocr_status || (data.donnees_ocr?.confiance > 0 ? "done" : r.ocr_status),
                fournisseur_nom: data.fournisseur_nom || data.donnees_ocr?.fournisseur_nom,
                montant_ttc: data.montant_ttc,
                numero_facture: data.numero_facture,
                ocr_error: data.ocr_error,
              };
            } catch {
              return r;
            }
          })
        );
        setResults(updated);
      } catch {
        // ignore
      }
    }, 3000);
    return () => clearTimeout(t);
  }, [polling, results]);

  const stats = useMemo(() => {
    const s = { done: 0, running: 0, queued: 0, error: 0 };
    results.forEach((r) => {
      if (!r.id) { s.error += 1; return; }
      if (r.ocr_status === "done") s.done += 1;
      else if (r.ocr_status === "running") s.running += 1;
      else if (r.ocr_status === "queued") s.queued += 1;
      else if (r.ocr_status === "error" || r.error) s.error += 1;
    });
    return s;
  }, [results]);

  const allDone = results.length > 0 && stats.done + stats.error === results.length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="ff-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#52a675]" /> Import en lot de factures fournisseurs
          </DialogTitle>
          <DialogDescription>
            Glissez jusqu&apos;à <strong>20 PDFs</strong> — chaque fichier deviendra une facture séparée.
            L&apos;analyse OCR se fait en arrière-plan (Claude Vision) : vous pouvez fermer cette fenêtre,
            les résultats seront prêts dans l&apos;onglet « À valider ».
          </DialogDescription>
        </DialogHeader>

        {results.length === 0 ? (
          <>
            <Card
              className="border-2 border-dashed border-gray-300 hover:border-[#52a675] transition-colors p-8 text-center cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              data-testid="ff-bulk-dropzone"
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <div className="text-sm text-gray-600">
                Glissez plusieurs PDFs ici, ou{" "}
                <span className="text-[#52a675] font-semibold underline">cliquez pour parcourir</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, HEIC · Max 20 · 15 Mo/fichier</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="ff-bulk-file-input"
              />
            </Card>

            {files.length > 0 && (
              <div className="mt-3 max-h-64 overflow-y-auto border rounded-lg divide-y">
                {files.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`ff-bulk-file-${idx}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{f.file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {(f.file.size / 1024 / 1024).toFixed(2)} Mo
                      </span>
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => removeFile(idx)}
                      className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                      data-testid={`ff-bulk-remove-${idx}`}
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                <Check className="w-3 h-3 mr-1" /> {stats.done} prête{stats.done > 1 ? "s" : ""}
              </Badge>
              {stats.running > 0 && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> {stats.running} en analyse
                </Badge>
              )}
              {stats.queued > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                  {stats.queued} en attente
                </Badge>
              )}
              {stats.error > 0 && (
                <Badge className="bg-red-100 text-red-800 border-red-200">
                  <XIcon className="w-3 h-3 mr-1" /> {stats.error} en erreur
                </Badge>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Fichier</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Fournisseur</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, idx) => (
                    <tr key={r.id || idx} className="border-t border-gray-100" data-testid={`ff-bulk-result-${r.id || idx}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[220px]" title={r.filename}>{r.filename}</span>
                        </div>
                        {r.reference_interne && (
                          <div className="text-[10px] text-gray-400 font-mono ml-5">{r.reference_interne}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.error ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" title={r.error}>
                            <XIcon className="w-3 h-3 mr-1" /> {r.error?.slice(0, 30)}
                          </Badge>
                        ) : r.ocr_status === "done" ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                            <Check className="w-3 h-3 mr-1" /> Prête
                          </Badge>
                        ) : r.ocr_status === "running" ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Analyse…
                          </Badge>
                        ) : r.ocr_status === "error" ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" title={r.ocr_error}>
                            <XIcon className="w-3 h-3 mr-1" /> Échec OCR
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                            En attente
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {r.fournisseur_nom || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {r.montant_ttc != null ? `${fmtChf(r.montant_ttc)} CHF` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.id && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => { navigate(`/factures-fournisseurs/${r.id}`); onClose(); }}
                            className="h-7 px-2"
                            data-testid={`ff-bulk-open-${r.id}`}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {allDone && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                Analyse terminée. Retrouvez toutes les factures dans l&apos;onglet « À valider » pour vérification et validation.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {results.length === 0 ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={uploading}>Annuler</Button>
              <Button
                onClick={startUpload}
                disabled={!files.length || uploading}
                className="bg-[#52a675] hover:bg-[#3d8a5c] text-white"
                data-testid="ff-bulk-start"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? "Import…" : `Lancer l'analyse de ${files.length} fichier${files.length > 1 ? "s" : ""}`}
              </Button>
            </>
          ) : (
            <Button
              onClick={onClose}
              className="bg-[#52a675] hover:bg-[#3d8a5c] text-white"
              data-testid="ff-bulk-close"
            >
              Fermer et rafraîchir
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
